<script setup lang="ts">
import { ItemInfoProps, useItemInfoComposable } from '@components/composables/item-info-composable.js';
import ProductImage from '@src/product/components/product-image.vue';
import ManufacturerLogo from '@src/manufacturer/components/manufacturer-logo.vue';
import VerticalAlignChildrenWrapper from '@components/layout-wrapper/vertical-align-children-wrapper.vue';
import CenterChildrenWrapper from '@components/layout-wrapper/center-children-wrapper.vue';
import HorizontalAlignChildrenWrapper from '@components/layout-wrapper/horizontal-align-children-wrapper.vue';
import Tag from '@components/tag.vue';
import ItemLoader from '@components/loader/item-loader.vue';
import Headline from '@components/headline.vue';
import Text from '@components/text.vue';
import useProductComposable from '@src/product/composables/product-composable.js';
import Link from '@components/link.vue';
import ButtonIcon from '@components/buttons/button-icon.vue';

const { withoutLinks = false } = defineProps<ItemInfoProps & {
    withoutLinks?: boolean,
}>();

const { product, loading } = useItemInfoComposable();

const { getShopLink, getItemInfoLink } = useProductComposable();
</script>

<template>
    <HorizontalAlignChildrenWrapper v-if="!loading" gap="4" grow-children>
        <VerticalAlignChildrenWrapper gap-bottom="2" :avoid-outer-gap="true" :spread="false">
            <ManufacturerLogo v-if="product.productAbstract.manufacturer" :id-manufacturer="product.productAbstract.manufacturer.id" size="xs"/>
            <CenterChildrenWrapper>
                <ProductImage :product="product" size="m"/>
            </CenterChildrenWrapper>
        </VerticalAlignChildrenWrapper>
        <VerticalAlignChildrenWrapper gap-bottom="2">
            <HorizontalAlignChildrenWrapper gap="2">
                <Headline type="h3">{{ product.name }}</Headline>
                <div>
                    <Tag v-if="product.serialNumberRequired" tag-value="SERIAL" tag-color="green"/>
                    <Tag v-else tag-value="NUK" tag-color="yellow"/>
                </div>
            </HorizontalAlignChildrenWrapper>
            <HorizontalAlignChildrenWrapper>
                <VerticalAlignChildrenWrapper>
                    <Text v-if="product.productAbstract.manufacturer" gap="1">Hersteller: {{ product.productAbstract.manufacturer.name }}</Text>
                    <Text gap="1">Produktgruppe: {{ product.productAbstract.productGroup ? product.productAbstract.productGroup.name : 'Nicht zugeordnet' }}</Text>
                    <Text gap="1">Produktart: {{ product.productAbstract.productKind ? product.productAbstract.productKind.name : 'Nicht zugeordnet' }}</Text>
                    <Text v-if="product.productAbstract.season" gap="1">Saison: {{ product.productAbstract.season }}</Text>
                    <Text v-if="product.productAbstract.colorPrimary" gap="1">Farbe: {{ product.productAbstract.colorPrimary }}</Text>
                    <Text v-if="product.productAbstract.model" gap="1">Modell: {{ product.productAbstract.model }}</Text>
                    <Text v-if="product.productAbstract.modelconf" gap="1">Modell-Detail: {{ product.productAbstract.modelconf }}</Text>
                </VerticalAlignChildrenWrapper>
                <VerticalAlignChildrenWrapper>
                    <Text gap="1">SKU: {{ product.sku }}</Text>
                    <Text gap="1">MPN: {{ product.firstMpn }}</Text>
                    <Text gap="1">EAN: {{ product.firstEan }}</Text>
                </VerticalAlignChildrenWrapper>
            </HorizontalAlignChildrenWrapper>
            <HorizontalAlignChildrenWrapper v-if="!withoutLinks" gap="1">
                <Link
                    :href="getItemInfoLink(product.sku)"
                    :force-new-tab="true"
                >
                    <ButtonIcon icon="share-square" spread>Link ins ZED</ButtonIcon>
                </Link>
                <Link
                    v-if="product.productAbstract.defaultUrl"
                    :href="getShopLink(product.productAbstract.defaultUrl)"
                    :force-new-tab="true"
                >
                    <ButtonIcon icon="shopping-cart" spread>Link zum Shop</ButtonIcon>
                </Link>
            </HorizontalAlignChildrenWrapper>
        </VerticalAlignChildrenWrapper>
    </HorizontalAlignChildrenWrapper>
    <CenterChildrenWrapper v-else>
        <ItemLoader :static-loader="true"/>
    </CenterChildrenWrapper>
</template>
